/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { useOktaAuth, withOktaAuth } from '@okta/okta-react';
import { withTranslation } from 'react-i18next';
import type { AuthState, OktaAuth } from '@okta/okta-auth-js';
import OktaSignIn from '@okta/okta-signin-widget';
import '@okta/okta-signin-widget/css/okta-sign-in.min.css';
import oktaLogo from '@assets/images/signin-logo.svg';
import Loader from '@atoms/Loader';

interface LoginProps {
  t: (key: string, options?: unknown) => string;
}

const oktaConfig = {
  issuer: import.meta.env.VITE_APP_OKTA_ISSUER as string,
  clientId: import.meta.env.VITE_APP_OKTA_CLIENT_ID as string,
  redirectUri: import.meta.env.VITE_APP_OKTA_REDIRECT_URI as string,
  baseURL: import.meta.env.VITE_APP_BASE_URL as string,
  scopes: ['openid', 'profile', 'email', 'groups'],
  pkce: true,
};

interface OktaTransactionContext {
  expiresAt?: string;
  status: string;
  sessionToken?: string;
  _embedded?: {
    user?: {
      id: string;
      profile: {
        login: string;
        firstName: string;
        lastName: string;
        [key: string]: any;
      };
    };
  };
  [key: string]: any;
}

const LoginWidget: React.FC<LoginProps & { authState: AuthState; oktaAuth: OktaAuth }> = (
  props
) => {
  const { oktaAuth } = useOktaAuth();
  const { t } = props;
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  useEffect(() => {
    const widget = new OktaSignIn({
      el: '#okta-login-container',
      useClassicEngine: true,
      useInteractionCodeFlow: false,
      baseUrl: oktaConfig.baseURL,
      clientId: oktaConfig.clientId,
      redirectUri: oktaConfig.redirectUri,
      logo: oktaLogo,
      features: {
        rememberMe: false,
        help: false,
        recovery: true,
        showEnrollment: true,
        showPasswordToggleOnSignInPage: true,
      },
      customButtons: [
        {
          title: `Don’t have an account yet? Register`,
          className: 'register-link',
          click: function () {
            window.location.href = '/register';
          },
        },
      ],
      i18n: {
        en: {
          'primaryauth.title': t('Login'),
          'password.forgot.email.placeholder': t('Email Address'),
          'password.forgot.email.tooltip': t('Email Address'),
          'password.forgot.emailSent.desc': t(
            'Email has been sent to {0} with password reset instructions.'
          ),
          'primaryauth.username.placeholder': t('Email Address'),
          'primaryauth.username.tooltip': t('Email Address'),
          'error.username.required': t('Please enter an Email Address'),
          'error.password.required': t('Please enter a Password'),
          forgotpassword: 'Forgot Password?',
          'primaryauth.submit': t('Login'),
          'registration.signup': 'Register',
          'errors.E0000004': 'Incorrect username or password',
        },
      },

      colors: {
        brand: '#05417B',
      },
      authParams: {
        pkce: oktaConfig.pkce,
        issuer: oktaConfig.issuer,
        scopes: oktaConfig.scopes,
      },
      transformUsername: (username: string) => {
        const loginPrefix = 'AUSPL__';
        return username.includes(loginPrefix) || !username.includes('@')
          ? username
          : loginPrefix + username;
      },
    });

    void widget.showSignInAndRedirect();

    widget.on('afterRender', () => {
      const btn = document.querySelector('.register-link');
      if (btn && !btn.querySelector('span')) {
        btn.innerHTML = btn.innerHTML.replace(
          'Register',
          '<span style="text-decoration: underline;">Register</span>'
        );
      }

      const passwordInputField = document.querySelector<HTMLInputElement>(
        '.primary-auth-form  input[type="password"]'
      );
      if (passwordInputField && passwordInputField.className == 'password-with-toggle') {
        const style = document.createElement('style');
        style.innerHTML = `
            .primary-auth-form input.password-with-toggle::-ms-reveal,
            .primary-auth-form input.password-with-toggle::-ms-clear {
              display: none !important;
            }
          `;
        document.head.appendChild(style);
      }
    });

    document.addEventListener('click', (e) => {
      const target = e.target as Element;
      if (target.matches('input[value="Verify"]')) {
        const code =
          (document.querySelector('input[name="answer"]') as HTMLInputElement)?.value ||
          (document.querySelector('input[name="passCode"]') as HTMLInputElement)?.value;

        if (code && code != '') {
          setLoader(true);
        }
      } else if (target.matches('input[value="Login"]')) {
        if (target.matches('input[value="Login"]')) {
          const username = (document.querySelector('input[name="username"]') as HTMLInputElement)
            .value;
          const password = (document.querySelector('input[name="password"]') as HTMLInputElement)
            .value;

          if (username && username.trim() != '' && password) {
            setLoader(true);
          }
        }
      }
    });

    widget.on('afterRender', (context: any) => {
      setLoader(false);
      const messages = context?.controller?.options?.appState?.get('messages');
      if (messages && messages.value.length > 0) {
        setLoader(false);
      }
    });

    widget.on('afterError', () => {
      setLoader(false);
    });

    return () => {
      widget.remove();
    };
  }, [oktaAuth]);

  return (
    <div className="login-page">
      <div className="login-container">
        <Loader display={loader} />
        <div id="okta-login-container" className="widget-wrapper" ref={widgetRef}></div>
      </div>
    </div>
  );
};

const TranslatedLoginPage = withTranslation()(LoginWidget);
export default withOktaAuth(TranslatedLoginPage);
